import { useId, type ChangeEvent, type CSSProperties, type SelectHTMLAttributes } from 'react';
import {
  canConvertUnit,
  convertiblePackages,
  convertibleUnits,
  densityOf,
  eachWeightOf,
  formatNumber,
  getUnit,
  type ConvertibleIngredient,
  type IngredientPackage,
  type UnitsBySystem,
} from '@crave/costing';

/** An ingredient row from GET /admin/recipes/ingredients (or a stock row with the same fields). */
export interface UnitIngredient extends ConvertibleIngredient {
  id?: string | undefined;
  convertible_units?: UnitsBySystem | null | undefined;
  display_unit?: string | null | undefined;
}

/** What the API takes on a line: exactly one of unit or package_id (or neither when nothing is chosen). */
export type UnitFields = { unit: string } | { package_id: string } | Record<string, never>;

/** 'unit:<code>' or 'package:<id>' from API fields. */
export function unitValue(unit?: string | null, packageId?: string | null): string {
  if (packageId) return `package:${packageId}`;
  if (unit) return `unit:${unit}`;
  return '';
}

/** Splits a picker value into the API's fields, ready to spread into a request body. */
export function splitUnitValue(value: string | null | undefined): UnitFields {
  if (value?.startsWith('package:')) {
    const id = value.slice('package:'.length);
    return id ? { package_id: id } : {};
  }
  if (value?.startsWith('unit:')) {
    const code = value.slice('unit:'.length);
    return code ? { unit: code } : {};
  }
  return {};
}

/** The unit an ingredient is normally counted in: its display_unit when it converts, else its base unit. */
export function defaultUnitValue(ingredient: UnitIngredient | null | undefined): string {
  if (!ingredient) return '';
  if (ingredient.display_unit && canConvertUnit(ingredient.display_unit, ingredient)) {
    return unitValue(ingredient.display_unit);
  }
  return unitValue(ingredient.base_unit);
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Pound (lb)", "Cup", "Imperial pint (imp pt)". */
export function unitLabel(code: string): string {
  const def = getUnit(code);
  if (!def) return code;
  return def.name === def.abbr ? capital(def.name) : `${capital(def.name)} (${def.abbr})`;
}

function packageLabel(p: IngredientPackage): string {
  const abbr = getUnit(p.unit)?.abbr ?? p.unit;
  const size = `${formatNumber(Number(p.quantity), 3)} ${abbr}`;
  return p.label.toLowerCase().includes(size.toLowerCase()) ? p.label : `${p.label} (${size})`;
}

/** A readable name for a picker value: "Pound (lb)" or "28 oz can". */
export function unitChoiceLabel(
  value: string,
  ingredient: UnitIngredient | null | undefined,
): string {
  const fields = splitUnitValue(value);
  if ('package_id' in fields) {
    const p = ingredient?.packages?.find((x) => x.id === fields.package_id);
    return p ? packageLabel(p) : 'Package';
  }
  if ('unit' in fields) return unitLabel(fields.unit);
  return '';
}

function unitsOf(ingredient: UnitIngredient): UnitsBySystem {
  if (
    ingredient.base_unit === 'g' ||
    ingredient.base_unit === 'ml' ||
    ingredient.base_unit === 'each'
  )
    return convertibleUnits(ingredient);
  return ingredient.convertible_units ?? { metric: [], us: [], imperial: [] };
}

/**
 * Why the everyday units for this kind of ingredient are missing, in plain words, or null when they
 * convert: cups for something weighed, weights for a liquid, weights for something counted.
 * "Set how much 1 cup of Honey weighs on the Ingredients page to use cups and spoons."
 */
export function unavailableUnitsHint(ingredient: UnitIngredient | null | undefined): string | null {
  if (!ingredient) return null;
  const name = ingredient.name?.trim() || 'this ingredient';
  const where = 'on the Ingredients page';
  switch (ingredient.base_unit) {
    case 'g':
      return densityOf(ingredient) === null
        ? `Set how much 1 cup of ${name} weighs ${where} to use cups and spoons.`
        : null;
    case 'ml':
      return densityOf(ingredient) === null
        ? `Set how much 1 cup of ${name} weighs ${where} to use grams and pounds.`
        : null;
    case 'each':
      return eachWeightOf(ingredient) === null
        ? `Set how much one ${name} weighs ${where} to use grams and pounds.`
        : null;
    default:
      return null;
  }
}

const controlStyle = (error: boolean): CSSProperties => ({
  width: '100%',
  minWidth: 0,
  minHeight: 'var(--touch)',
  height: 'var(--touch)',
  padding: '0 10px',
  borderRadius: 'var(--radius-sm)',
  border: `2px solid ${error ? 'var(--crave-red)' : 'var(--line)'}`,
  background: 'var(--paper)',
  color: 'var(--ink)',
});

export interface UnitSelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'children' | 'value'
> {
  /** The ingredient whose units to offer. With none, the picker is empty and disabled. */
  ingredient: UnitIngredient | null | undefined;
  /** 'unit:<code>', 'package:<id>' or ''. */
  value: string;
  /** Gets the new value and the API fields ({ unit } or { package_id }). onChange still works too. */
  onValueChange?: ((value: string, fields: UnitFields) => void) | undefined;
  label?: string | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  /** Spans the whole FormGrid row. */
  wide?: boolean | undefined;
  /** Shown as the empty first option; omit to have no empty option. */
  placeholder?: string | undefined;
  /** Offer the ingredient's packages (default true). */
  includePackages?: boolean | undefined;
  /** Explain missing cups or counts under the picker (default true). */
  showUnavailableHint?: boolean | undefined;
}

/**
 * A unit picker for one ingredient, laid out like SelectField (label, control, hint rows that line
 * up inside a FormGrid): only units that convert, grouped Metric, US, Imperial, Count and Packages.
 */
export function UnitSelect({
  ingredient,
  value,
  onValueChange,
  onChange,
  label = 'Unit',
  hint,
  error,
  wide,
  placeholder,
  includePackages = true,
  showUnavailableHint = true,
  id,
  style,
  className,
  disabled,
  ...rest
}: UnitSelectProps) {
  const autoId = useId();
  const selectId = id ?? autoId;

  const groups: { label: string; options: [string, string][] }[] = [];
  if (ingredient) {
    const units = unitsOf(ingredient);
    const counts = new Set<string>();
    const kindOf = (code: string) => getUnit(code)?.kind;
    const add = (groupLabel: string, codes: readonly string[]) => {
      const options = codes
        .filter((c) => {
          if (kindOf(c) !== 'count') return true;
          counts.add(c);
          return false;
        })
        .map((c): [string, string] => [unitValue(c), unitLabel(c)]);
      if (options.length) groups.push({ label: groupLabel, options });
    };
    add('Metric', units.metric);
    add('US', units.us);
    add('Imperial', units.imperial);
    if (counts.size) {
      groups.push({
        label: 'Count',
        options: [...counts].map((c): [string, string] => [unitValue(c), unitLabel(c)]),
      });
    }
    if (includePackages) {
      const packages = convertiblePackages(ingredient);
      if (packages.length) {
        groups.push({
          label: 'Packages',
          options: packages.map((p): [string, string] => [unitValue(null, p.id), packageLabel(p)]),
        });
      }
    }
  }
  const known = groups.some((g) => g.options.some(([v]) => v === value));
  // A saved unit that no longer converts stays visible rather than the picker going blank.
  const stale = value && !known ? unitChoiceLabel(value, ingredient) : '';

  const autoHint = showUnavailableHint ? unavailableUnitsHint(ingredient) : null;
  const shownHint = error ?? ([hint, autoHint].filter(Boolean).join(' ') || '');

  const handle = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange?.(e);
    onValueChange?.(e.target.value, splitUnitValue(e.target.value));
  };

  return (
    <label
      htmlFor={selectId}
      className={['crave-field', wide ? 'crave-field-wide' : '', className]
        .filter(Boolean)
        .join(' ')}
      style={style}
    >
      <span className="crave-field-label">{label}</span>
      <select
        id={selectId}
        aria-invalid={error ? true : undefined}
        aria-describedby={shownHint ? `${selectId}-desc` : undefined}
        {...rest}
        value={value}
        onChange={handle}
        disabled={disabled || !ingredient}
        style={controlStyle(Boolean(error))}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {stale && (
          <optgroup label="Saved">
            <option value={value}>{stale} (needs setup)</option>
          </optgroup>
        )}
        {groups.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.options.map(([v, l]) => (
              <option key={`${g.label}-${v}`} value={v}>
                {l}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <span
        id={`${selectId}-desc`}
        className="crave-field-hint"
        style={{ color: error ? 'var(--crave-red)' : undefined }}
      >
        {shownHint}
      </span>
    </label>
  );
}
